import { useTranslation } from 'react-i18next'

import { Trans } from '@/components'

import type { ResourceCompatibility } from '../resourceCompatibility'

interface BootSourceCompatibilityMessageProps {
  compatibility: ResourceCompatibility
  sourceType: 'image' | 'snapshot'
  supportsExtraResources: boolean
}

function formatGB(ramMB: number) {
  const ramGB = ramMB / 1024

  return Number.isInteger(ramGB) ? String(ramGB) : ramGB.toFixed(1)
}

export function BootSourceCompatibilityMessage({
  compatibility,
  sourceType,
  supportsExtraResources,
}: BootSourceCompatibilityMessageProps) {
  const { t } = useTranslation()
  const sourceTypeLabel = t(`create.bootSourceType.${sourceType}`)

  if (!supportsExtraResources) {
    return (
      <div className="max-w-[180px] text-center t-xs text-white">
        <Trans
          i18nKey="create.bootSourceUnavailableForSubscription"
          values={{ sourceType: sourceTypeLabel }}
        >
          This {'{{sourceType}}'} requires extra resources which are not supported for subscription
        </Trans>
      </div>
    )
  }

  return (
    <div className="max-w-[320px] text-left">
      <div className="t-sm">
        <Trans
          i18nKey="create.bootSourceUnavailableForMachine"
          values={{ sourceType: sourceTypeLabel }}
        >
          This {'{{sourceType}}'} requires more resources than this machine can provide.
        </Trans>
      </div>

      <ul className="mt-2 list-disc space-y-1 pl-4 t-xs">
        {compatibility.cpuInsufficient ? (
          <li>
            <Trans
              i18nKey="create.bootSourceRequirementCpu"
              values={{
                required: compatibility.required.cpuCores,
                available: compatibility.available.cpuCores,
              }}
            >
              vCPU: requires {'{{required}}'}, provides {'{{available}}'}
            </Trans>
          </li>
        ) : null}
        {compatibility.ramInsufficient ? (
          <li>
            <Trans
              i18nKey="create.bootSourceRequirementRam"
              values={{
                required: formatGB(compatibility.required.ramMB),
                available: formatGB(compatibility.available.ramMB),
              }}
            >
              RAM: requires {'{{required}}'} GB, provides {'{{available}}'} GB
            </Trans>
          </li>
        ) : null}
        {compatibility.diskInsufficient ? (
          <li>
            <Trans
              i18nKey="create.bootSourceRequirementStorage"
              values={{
                required: compatibility.required.diskGB,
                available: compatibility.available.diskGB,
              }}
            >
              Storage: requires {'{{required}}'} GB, provides {'{{available}}'} GB
            </Trans>
          </li>
        ) : null}
      </ul>

      <div className="mt-2 t-xs">
        <Trans i18nKey="create.bootSourceMachineRecovery" values={{ sourceType: sourceTypeLabel }}>
          Choose another {'{{sourceType}}'} or machine.
        </Trans>
      </div>
    </div>
  )
}
